import { FC } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { BulkOperationsButton } from "@ryde/controls";
import { useSelectedIds } from "@ryde/redux";
import {
  GenericExportFormat,
  IGenericExportDrawerProps,
  IInjectedDrawerProps,
} from "@ryde/shared";

import { BulkButtonsPanelStyled } from "../../../right-drawers/client-side-bulk-operation-drawer-flow/styles";
import { CommonDefaultDrawer } from "../common-default-drawer";
import {
  BaseDrawer,
  DrawerEntityDataLoader,
  DrawerHeaderWithSubtitle,
  FormFooter,
} from "../drawer";
import { DrawerGenericExportWrapperStyled } from "./generic-export.styled";
import { useGenericExport } from "./use-generic-export";

export const GenericExportDrawer: FC<
  IGenericExportDrawerProps & IInjectedDrawerProps
> = ({
  drawerNumber,
  onCloseDrawer,
  entity,
  selectedRowsData,
  selectedIds,
}) => {
  const {
    rowsData,
    isFetchingRows,
    csvData,
    loading,
    format,
    setFormat,
    wrapperRef,
    currentEntityName,
    handleExport,
  } = useGenericExport({ entity, selectedRowsData, selectedIds });

  const { removeSelectedIds } = useSelectedIds(entity);
  const { t } = useTranslation();

  const handleCloseDrawer = useEvent((): void => {
    onCloseDrawer();
    // Remove only the rows that were exported, keeping any other selected rows.
    const exportedIds = rowsData.length
      ? rowsData.map(row => row.id)
      : (selectedIds ?? selectedRowsData.map(row => row.id));
    removeSelectedIds(exportedIds);
  });

  const isBusy = loading || isFetchingRows;

  return (
    <BaseDrawer onCloseDrawer={handleCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        onCloseDrawer={handleCloseDrawer}
        Header={
          <DrawerHeaderWithSubtitle
            title={t("general.EXPORT_CSV_EXCEL")}
            subTitle={` ( ${currentEntityName} ) `}
            onCloseDrawer={handleCloseDrawer}
          />
        }
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={handleCloseDrawer}
          />
        }
      >
        <DrawerGenericExportWrapperStyled>
          {!isBusy && (
            <>
              <div
                className="export-format-toggle"
                data-e2e="export-format-toggle"
                role="radiogroup"
              >
                <button
                  className="export-format-toggle__button"
                  type="button"
                  role="radio"
                  aria-checked={format === GenericExportFormat.Csv}
                  onClick={() => setFormat(GenericExportFormat.Csv)}
                >
                  {t("general.EXPORT_FORMAT_CSV")}
                </button>
                <button
                  className="export-format-toggle__button"
                  type="button"
                  role="radio"
                  aria-checked={format === GenericExportFormat.Xlsx}
                  onClick={() => setFormat(GenericExportFormat.Xlsx)}
                >
                  {t("general.EXPORT_FORMAT_EXCEL")}
                </button>
              </div>

              <BulkButtonsPanelStyled>
                <BulkOperationsButton
                  onClick={() => handleExport(true)}
                  isDisabled={isBusy || !rowsData?.length}
                >
                  {t("general.EXPORT_ALL_FIELDS")}
                </BulkOperationsButton>

                <BulkOperationsButton
                  onClick={() => handleExport(false)}
                  isDisabled={isBusy || !rowsData?.length}
                >
                  {t("general.EXPORT_CURRENT_VIEW_FIELDS")}
                </BulkOperationsButton>
              </BulkButtonsPanelStyled>
            </>
          )}

          {isBusy && <DrawerEntityDataLoader entityTitle={currentEntityName} />}

          {/* Wrap CSVLink so we can get its internal anchor */}
          <div ref={wrapperRef} style={{ display: "none" }}>
            <CSVLink data={csvData} filename={`${currentEntityName}.csv`} />
          </div>
        </DrawerGenericExportWrapperStyled>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
